import { isAnswerable, type FrozenSlide } from "./deck";
import type { HostLiveState, OpenRoundResult, SessionSettings } from "./host";
import type { QnaQuestion } from "./qna";
import type { JoinedName, LeaderboardEntry, Reaction, ServerEvent, Stage } from "./realtime";

/**
 * A live session as a presenter's screen holds it — the console, the audience screen,
 * the phone remote — and what the presenter's Next will do from there.
 *
 * Here rather than in any one app because three of them read the same event stream
 * and must agree on what it means: built from `GET /sessions/:id/live`, kept current
 * by the room and host channels (liveReducer), and stepped through by the same rule
 * SessionService.advance applies (nextStep). Pure — no network, no clock, no
 * framework.
 */

export type Round = {
  roundId: string;
  slideId: string;
  /**
   * When answers open, on this browser's clock: a quiz question is shown this long
   * before it can be answered (readingDelayMs). Until then the room reads it.
   */
  opensAt: number;
  /** On this browser's clock, corrected for the server's (see `clockOffsetMs`). */
  deadline: number | null;
  closed: boolean;
  revealed: boolean;
  counts: Record<string, number>;
  responseCount: number;
  /** Free-text slides: the latest answers, newest first. */
  texts: string[];
  /** A scored round's highlights, once it has closed (`round.stats`). */
  fastest?: { participantId: string; displayName: string | null; responseMs: number } | null;
  correctCount?: number;
};

export type LiveState = {
  status: HostLiveState["session"]["status"];
  stage: Stage;
  currentId: string | null;
  round: Round | null;
  leaderboard: LeaderboardEntry[];
  participantCount: number;
  /** The latest names to join, newest first: who a quiz's lobby shows. */
  joined: JoinedName[];
  /** Whether the lobby's QR code fills the screen, for scanning from the back of the room. */
  qrLarge: boolean;
  questions: QnaQuestion[];
  /**
   * The leaderboard as it stood when the current round opened — what "climbed three
   * places" is measured against. The leaderboard itself changes only when a round
   * closes, so this is the table before the question the room just answered.
   */
  baseline: LeaderboardEntry[];
  /** The latest batch of audience reactions, numbered so a screen can tell a new one from the last. */
  reactions: { seq: number; counts: Partial<Record<Reaction, number>> } | null;
};

/** The round belonging to the slide on screen, if there is one. */
export function roundOnScreen(state: LiveState, slide: FrozenSlide | null): Round | null {
  return state.round && slide && state.round.slideId === slide.id ? state.round : null;
}

/**
 * What pressing "Next" will do from here — the same decision SessionService.advance
 * makes, so the button can say it before it is pressed.
 */
export type NextStep =
  | { kind: "start" }
  | { kind: "open" }
  | { kind: "close" }
  | { kind: "closeAndNext"; then: "slide" | "podium" | "end" }
  | { kind: "leaderboard" }
  | { kind: "slide"; next: FrozenSlide }
  | { kind: "podium" }
  | { kind: "finish" };

/**
 * Does the lobby show who has joined? In a quiz, where the names are going on the
 * leaderboard anyway, seeing your own come up is how you know you are in. Not in a
 * session that keeps no names, and not in a poll or Q&A, where nobody is ranked.
 */
export function showsJoinedNames(slides: FrozenSlide[], settings: SessionSettings): boolean {
  return !settings.anonymous && hasHallOfFame(slides, settings);
}

/** Does this session end on a hall of fame? The server's rule, in SessionService.advance. */
export function hasHallOfFame(slides: FrozenSlide[], settings: SessionSettings): boolean {
  return settings.showLeaderboard && slides.some((s) => s.isScored);
}

export function nextStep(state: LiveState, slides: FrozenSlide[], settings: SessionSettings): NextStep | null {
  if (state.status === "lobby") return { kind: "start" };
  if (state.status === "ended") return null;
  if (state.stage.phase === "podium") return { kind: "finish" };

  const index = slides.findIndex((s) => s.id === state.stage.slideId);
  const slide = slides[index] ?? null;
  const following = slides[index + 1] ?? null;
  const round = roundOnScreen(state, slide);
  const finale = hasHallOfFame(slides, settings);

  if (slide && state.stage.phase === "slide" && isAnswerable(slide.type)) {
    if (!round) return { kind: "open" };
    if (!round.closed) {
      if (slide.isScored) return { kind: "close" };
      return { kind: "closeAndNext", then: following ? "slide" : finale ? "podium" : "end" };
    }
    if (slide.isScored && settings.showLeaderboard && following) return { kind: "leaderboard" };
  }
  if (following) return { kind: "slide", next: following };
  return finale ? { kind: "podium" } : { kind: "finish" };
}

export type LiveAction =
  | { kind: "event"; event: ServerEvent; clockOffsetMs: number }
  | { kind: "stage"; stage: Stage }
  | { kind: "opened"; result: OpenRoundResult; clockOffsetMs: number }
  | { kind: "question"; question: QnaQuestion }
  | { kind: "qr"; large: boolean }
  /** This screen's clock was found to be off by more than it thought: move what it shows. */
  | { kind: "clock"; shiftMs: number };

function deadlineOf(openedAt: string, timeLimitMs: number, clockOffsetMs: number): number | null {
  return timeLimitMs > 0 ? Date.parse(openedAt) + timeLimitMs - clockOffsetMs : null;
}

/**
 * Whole seconds until a round's answers open, or 0 once they have: the room is
 * reading the question, and nothing — not "Next", not "Stop answers" — moves it on.
 */
export function readingLeft(round: Pick<Round, "opensAt" | "closed"> | null, now: number): number {
  if (!round || round.closed || now >= round.opensAt) return 0;
  return Math.ceil((round.opensAt - now) / 1000);
}

/** A round opening, from the event or from our own action — whichever lands first. */
function opened(state: LiveState, r: OpenRoundResult, clockOffsetMs: number): LiveState {
  // The same round arriving twice keeps the counts it has already gathered.
  if (state.round?.roundId === r.roundId) return { ...state, currentId: r.slideId };
  return {
    ...state,
    baseline: state.leaderboard,
    currentId: r.slideId,
    round: {
      roundId: r.roundId,
      slideId: r.slideId,
      opensAt: Date.parse(r.openedAt) - clockOffsetMs,
      deadline: deadlineOf(r.openedAt, r.timeLimitMs, clockOffsetMs),
      closed: false,
      revealed: false,
      counts: {},
      responseCount: 0,
      texts: [],
    },
  };
}

/**
 * Stages only move forward. A broadcast can arrive after this screen's own "Next"
 * already reported a later step, and must not wind the screen back.
 */
function staged(state: LiveState, stage: Stage): LiveState {
  if (stage.step < state.stage.step) return state;
  return { ...state, status: "live", stage, currentId: stage.slideId ?? state.currentId };
}

/** Insert or replace one question, by id. */
function upsertQuestion(questions: QnaQuestion[], q: QnaQuestion): QnaQuestion[] {
  return [q, ...questions.filter((x) => x.questionId !== q.questionId)];
}

function patchRound(state: LiveState, roundId: string, patch: Partial<Round>): LiveState {
  if (state.round?.roundId !== roundId) return state;
  return { ...state, round: { ...state.round, ...patch } };
}

export function liveReducer(state: LiveState, action: LiveAction): LiveState {
  switch (action.kind) {
    case "stage":
      return staged(state, action.stage);
    case "opened":
      return opened(state, action.result, action.clockOffsetMs);
    case "question":
      return { ...state, questions: upsertQuestion(state.questions, action.question) };
    case "qr":
      return { ...state, qrLarge: action.large };
    case "clock":
      return state.round
        ? {
            ...state,
            round: {
              ...state.round,
              opensAt: state.round.opensAt + action.shiftMs,
              deadline: state.round.deadline === null ? null : state.round.deadline + action.shiftMs,
            },
          }
        : state;
    case "event": {
      const e = action.event;
      switch (e.type) {
        case "stage.changed":
          return staged(state, e.stage);
        case "slide.changed":
          return { ...state, currentId: e.slideId };
        case "round.opened":
          return opened(state, e, action.clockOffsetMs);
        case "reactions":
          return { ...state, reactions: { seq: (state.reactions?.seq ?? 0) + 1, counts: e.counts } };
        case "tally.updated":
          return patchRound(state, e.roundId, {
            counts: e.counts,
            responseCount: e.responseCount,
            ...(e.texts ? { texts: e.texts } : {}),
          });
        case "round.closed":
          return patchRound(state, e.roundId, { closed: true });
        case "results.revealed":
          return patchRound(state, e.roundId, { closed: true, revealed: true, counts: e.tallies });
        case "round.stats":
          return patchRound(state, e.roundId, { fastest: e.fastest, correctCount: e.correctCount });
        case "leaderboard.updated":
          return { ...state, leaderboard: e.entries };
        case "lobby.qr":
          return { ...state, qrLarge: e.large };
        case "participants.updated":
          return { ...state, participantCount: e.count, ...(e.recent ? { joined: e.recent } : {}) };
        case "qna.question":
          return { ...state, questions: upsertQuestion(state.questions, e.question) };
        case "qna.updated":
          return {
            ...state,
            questions: state.questions.map((q) =>
              q.questionId === e.questionId ? { ...q, status: e.status, upvoteCount: e.upvoteCount } : q,
            ),
          };
        default:
          // session.ended is handled by refreshing the page; score.updated belongs to
          // participants.
          return state;
      }
    }
  }
}

/** A screen's starting state, from the server's snapshot. */
export function liveStateFrom(initial: HostLiveState, clockOffsetMs: number): LiveState {
  const r = initial.round;
  return {
    status: initial.session.status,
    stage: initial.stage,
    currentId: initial.stage.slideId ?? r?.slideId ?? null,
    leaderboard: initial.leaderboard,
    participantCount: initial.session.participantCount,
    joined: initial.joined,
    qrLarge: initial.qrLarge,
    questions: initial.questions,
    baseline: initial.leaderboard,
    reactions: null,
    round: r && {
      roundId: r.roundId,
      slideId: r.slideId,
      opensAt: Date.parse(r.openedAt) - clockOffsetMs,
      deadline: deadlineOf(r.openedAt, r.timeLimitMs, clockOffsetMs),
      closed: r.closed,
      revealed: r.revealed,
      counts: r.counts,
      responseCount: r.responseCount,
      texts: r.texts,
    },
  };
}

/**
 * How far each person on the leaderboard has moved since the round opened: places
 * gained (negative for places lost), or "new" for someone who has just made the
 * table. Nobody is missing from the result — those who did not move get 0.
 */
export function rankChanges(baseline: LeaderboardEntry[], entries: LeaderboardEntry[]): Map<string, number | "new"> {
  const before = new Map(baseline.map((e) => [e.participantId, e.rank]));
  return new Map(
    entries.map((e) => {
      const was = before.get(e.participantId);
      return [e.participantId, was === undefined ? (baseline.length > 0 ? "new" : 0) : was - e.rank];
    }),
  );
}

/** The biggest climber since the round opened, if anyone climbed at all. Ties go to the higher place. */
export function biggestClimber(
  baseline: LeaderboardEntry[],
  entries: LeaderboardEntry[],
): { entry: LeaderboardEntry; places: number } | null {
  let best: { entry: LeaderboardEntry; places: number } | null = null;
  for (const e of entries) {
    const was = baseline.find((b) => b.participantId === e.participantId)?.rank;
    // Onto the table from outside it counts as climbing from just below it.
    const places = was === undefined ? (baseline.length > 0 ? baseline.length + 1 - e.rank : 0) : was - e.rank;
    if (places > 0 && (!best || places > best.places)) best = { entry: e, places };
  }
  return best;
}
